import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { History, RotateCcw, CheckCircle2, Loader2, Camera } from 'lucide-react';

/**
 * Milestone G — Playbook versions + rollback.
 *
 * Lists the last N snapshots of the active during-call playbook and lets
 * the user manually snapshot or rollback. Auto-snapshots happen server-side
 * before any rollback so the user can always undo.
 */
export default function BrainVersionsCard({ api }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/ava/versions');
      setVersions(data?.versions || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load versions.');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const snapshot = async () => {
    setBusy(true);
    try {
      await api.post('/ava/versions/snapshot', { label: 'manual' });
      toast.success('Snapshot saved. You can roll back to it any time.');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not snapshot.');
    } finally { setBusy(false); }
  };

  const rollback = async (id) => {
    setBusy(true);
    try {
      await api.post(`/ava/versions/${id}/rollback`);
      toast.success('Playbook restored. Ava will use it on the next call.');
      setConfirmId(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not roll back.');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="brain-versions-card" className="bg-white dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.06] rounded-xl p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-blue-500" />
            <h3 className="text-[15px] font-heading font-semibold text-gray-900 dark:text-white">Playbook versions</h3>
          </div>
          <p className="text-[12.5px] text-gray-500 dark:text-slate-400 mt-0.5">
            Roll back to a previous version of your during-call playbook. We auto-snapshot before every rollback.
          </p>
        </div>
        <button data-testid="brain-snapshot-btn" onClick={snapshot} disabled={busy}
          className="flex-shrink-0 text-xs font-medium text-white bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
          Snapshot now
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6"><Loader2 className="w-4 h-4 text-gray-400 animate-spin" /></div>
      ) : versions.length === 0 ? (
        <div data-testid="brain-versions-empty" className="bg-gray-50 dark:bg-white/[0.03] border border-dashed border-gray-200 dark:border-white/[0.08] rounded-lg p-4 text-center">
          <p className="text-[12.5px] text-gray-500 dark:text-slate-400">
            No snapshots yet. Snapshots are taken automatically the first time you edit your playbook, or you can take one manually right now.
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {versions.map((v) => {
            const when = v.created_at ? new Date(v.created_at).toLocaleString() : '—';
            const isConfirm = confirmId === v.id;
            return (
              <li key={v.id} data-testid={`brain-version-${v.id}`}
                className="flex items-center justify-between gap-3 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] rounded-lg px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10.5px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full ${
                      v.label === 'pre-rollback'
                        ? 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300'
                        : v.label === 'manual'
                        ? 'bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300'
                        : 'bg-gray-200 dark:bg-white/[0.06] text-gray-700 dark:text-slate-300'
                    }`}>
                      {v.label || 'auto'}
                    </span>
                    <span className="text-[12px] text-gray-700 dark:text-slate-200 truncate">{when}</span>
                  </div>
                </div>
                {isConfirm ? (
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => setConfirmId(null)} disabled={busy}
                      className="text-[11.5px] text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white px-2.5 py-1 rounded-md disabled:opacity-50">
                      Cancel
                    </button>
                    <button data-testid={`brain-rollback-confirm-${v.id}`} onClick={() => rollback(v.id)} disabled={busy}
                      className="text-[11.5px] font-medium text-white bg-amber-600 hover:bg-amber-500 disabled:opacity-50 rounded-md px-2.5 py-1 inline-flex items-center gap-1">
                      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Confirm rollback
                    </button>
                  </div>
                ) : (
                  <button data-testid={`brain-rollback-${v.id}`} onClick={() => setConfirmId(v.id)}
                    className="text-[11.5px] font-medium text-blue-600 dark:text-blue-300 hover:text-blue-700 dark:hover:text-blue-200 inline-flex items-center gap-1">
                    <RotateCcw className="w-3 h-3" /> Roll back
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
